import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch';
import {
	getFinalSelectionCount,
	getPageSize,
	getTableTotalCount,
	SelectionScope,
	setTableAllSelected,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Flex, Text, useMantineTheme } from '@mantine/core';
import { useEffect } from 'react';
import { Table } from '@tanstack/react-table';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';

export const SelectionHeader = <T extends Record<string, unknown>>({
	table,
}: {
	table: Table<T>;
}) => {
	const t = useTranslationCommonUi();
	const theme = useMantineTheme();

	const {
		greedySelecting,
		setGreedySelecting,
		selectionScope,
		setSelectionScope,
		selectAll,
		setSelectAll,
		manualPagination,
	} = getTableMeta(table);

	const totalSelected = getFinalSelectionCount(table);
	const totalRows = getTableTotalCount(table);
	const pageSize = getPageSize(table);

	const pageAllSelected = table.getIsAllPageRowsSelected();

	const missingAnyRows = totalSelected !== totalRows;
	const getSwitchPosition = (): boolean | null => {
		if (selectionScope === SelectionScope.All) {
			return missingAnyRows ? null : true;
		}
		if (selectionScope === SelectionScope.Page) {
			return pageAllSelected ? false : null;
		}
		return null;
	};

	const switchPos: boolean | null = getSwitchPosition();

	const pageIndex = table.getState().pagination.pageIndex;
	const rows = table.getRowModel().rows;

	useEffect(() => {
		if (manualPagination || !greedySelecting || selectionScope !== SelectionScope.All) {
			return;
		}

		const selected = table.getState().rowSelection;
		table.getRowModel().rows.forEach((row) => {
			if (selected[row.id] === undefined) {
				table.setRowSelection((prev) => ({ ...prev, [row.id]: true }));
			}
		});
	}, [greedySelecting, selectionScope, table, pageIndex, rows, manualPagination]);

	useEffect(() => {
		const shouldBe = !missingAnyRows && selectionScope === SelectionScope.All;
		if (selectAll !== shouldBe) {
			setSelectAll(shouldBe);
		}
	}, [missingAnyRows, selectionScope, selectAll, setSelectAll]);

	const flipScope = (value: boolean) => {
		const scope = value ? SelectionScope.All : SelectionScope.Page;
		setSelectionScope(scope);

		if (scope === SelectionScope.All) {
			// Switch to "All" mode - select all rows across all pages
			setGreedySelecting(true);
			setSelectAll(true);
			setTableAllSelected(table, true);
			table.setRowSelection({});
			// Mark all rows as selected by setting them to true in the selection state
			if (!manualPagination) {
				const allRowIds = table.getPrePaginationRowModel().rows.map((row) => row.id);
				const allSelectedState = allRowIds.reduce(
					(acc, id) => ({ ...acc, [id]: true }),
					{}
				);
				table.setRowSelection(allSelectedState);
			}
		} else {
			// Switch to "Page" mode - only select current page
			setGreedySelecting(false);
			setSelectAll(false);
			setTableAllSelected(table, false);
			// Clear all selections and only select current page
			table.resetRowSelection(true);
			table.toggleAllPageRowsSelected(true);
		}
	};

	return (
		<Flex
			gap="0.5rem"
			sx={{
				backgroundColor: theme.colors.gray[2],
				padding: `calc(${theme.spacing.xs} / 2) ${theme.spacing.sm}`,
				width: '100%',
				marginTop: -4,
			}}
			data-testid={TEST_ELEMENT_IDS.CAP_SELECTION_HEADER}
		>
			<AdvancedSwitch
				twoWay={true}
				selectedValue={switchPos}
				leftText={t('mantine_table.top_toolbar.selection_header.left_text', {
					selectionCount: pageSize.toString(),
				})}
				leftValue={false}
				rightText={t('mantine_table.top_toolbar.selection_header.right_text', {
					totalPages: Math.ceil(totalRows / pageSize).toString(),
				})}
				rightValue={true}
				onChange={flipScope}
				unselectedColor="dark.8"
			/>

			<Flex
				p="0.1rem 0.25rem"
				bg="gray.3"
				align="center"
				sx={{ borderRadius: 4 }}
				data-testid={TEST_ELEMENT_IDS.CAP_SELECTION_COUNT_DISPLAY}
			>
				<Text c="blue.6" fw={600} lh={1} fz={11}>
					{totalSelected} {t('mantine_table.top_toolbar.selection_header.items_selected')}
				</Text>
			</Flex>
		</Flex>
	);
};
